import {
  parseNovelAvatarPublication,
  parseNovelAvatarPublicationStatus,
  parseNovelArchiveResponse,
  parseNovelAssetResponse,
  parseNovelContentResponse,
  parseNovelCoverLocalesResponse,
  parseNovelDeleteResponse,
  parseNovelOpeningAssistResponse,
  parseNovelStudioExport,
  parseNovelStudioPackDetail,
  parseNovelStudioPackList,
  parseNovelStudioVersionList,
  parseNovelVersionExport,
  parseNovelVersionResponse,
  parseNovelTtsVoiceList,
  parseNovelValidateResponse,
  parseNovelVoicesResponse,
  parseNovelRunView,
  parseNovelReviewResponse,
  parseNovelReviewList,
  parseNovelAuthoringContext,
} from '@drama/contracts/novel'
import { createNovelRunsApi } from './runs.js'
import { createNovelTtsApi } from './tts.js'
import { createNovelDramaApi } from './drama.js'

// Remote media uploads can outlive the former two-minute browser deadline.
// Match the deployed API proxy window so completed uploads can reach the editor.
const ASSET_UPLOAD_TIMEOUT_MS = 300_000
const AVATAR_PUBLICATION_TIMEOUT_MS = 1_800_000
const PACKS = '/api/novel/studio/packs'

/** @param {string} pack_id */
const packPath = pack_id => `${PACKS}/${encodeURIComponent(pack_id)}`

/**
 * Story author-owned drafts, immutable versions, private runs and publication reviews.
 * @param {{ request: Function, binary: Function }} transport
 */
export function createNovelStudioApi(transport) {
  const playtest_options = { prefix: '/api/novel/studio/playtests' }
  const playtest_runs = createNovelRunsApi(transport, playtest_options)
  delete playtest_runs.createRun
  delete playtest_runs.listRuns
  return {
    authoringContext() {
      return transport.request('GET', '/api/novel/studio/context', { parse: parseNovelAuthoringContext })
    },
    /** Authenticated short MP3 sample; the server supplies the text and checks the voice language. */
    previewTtsVoice({ voice_id, language, signal }) {
      return transport.binary('POST', `/api/novel/studio/tts-voices/${encodeURIComponent(voice_id)}/preview`, {
        body: { language }, signal, timeoutMs: 90000,
      })
    },
    playtests: { runs: playtest_runs, tts: createNovelTtsApi(transport, playtest_options), drama: createNovelDramaApi(transport, playtest_options) },
    createPlaytest({ pack_id, ...body }) {
      return transport.request('POST', `${packPath(pack_id)}/playtests`, { body, parse: parseNovelRunView })
    },
    /** Freeze the saved revision into a normal private run with normal gameplay billing. */
    createPrivateRun({ pack_id, ...body }) {
      return transport.request('POST', `${packPath(pack_id)}/private-runs`, { body, parse: parseNovelRunView })
    },
    /** Submit the exact saved revision for a human publication review. */
    submitReview({ pack_id, ...body }) {
      return transport.request('POST', `${packPath(pack_id)}/reviews`, { body, parse: parseNovelReviewResponse })
    },
    reviewStatus({ pack_id }) {
      return transport.request('GET', `${packPath(pack_id)}/reviews`, { parse: parseNovelReviewList })
    },
    withdrawReview({ pack_id, review_id }) {
      return transport.request('POST', `${packPath(pack_id)}/reviews/${encodeURIComponent(review_id)}/withdraw`, { body: {}, parse: parseNovelReviewResponse })
    },
    /** Hide the work and withdraw outstanding publication reviews without deleting private runs. */
    makePrivate(pack_id) {
      return transport.request('POST', `${packPath(pack_id)}/private`, { body: {}, parse: parseNovelArchiveResponse })
    },
    /** Returns an opening proposal without changing the saved draft or published version. */
    assistOpening({ pack_id, ...body }) {
      return transport.request('POST', `${packPath(pack_id)}/opening-assist`, { body, parse: parseNovelOpeningAssistResponse, timeoutMs: 180_000 })
    },
    activateVersion({ pack_id, version_id }) {
      return transport.request('POST', `${packPath(pack_id)}/versions/${encodeURIComponent(version_id)}/activate`, { body: {}, parse: parseNovelArchiveResponse, timeoutMs: AVATAR_PUBLICATION_TIMEOUT_MS })
    },
    prepareAvatars({ pack_id, provider }) {
      return transport.request('POST', `${packPath(pack_id)}/avatar-publications`, { body: { provider }, parse: parseNovelAvatarPublication, timeoutMs: AVATAR_PUBLICATION_TIMEOUT_MS })
    },
    avatarPublicationStatus({ pack_id, version_id }) {
      const query = version_id ? `?${new URLSearchParams({ version_id })}` : ''
      return transport.request('GET', `${packPath(pack_id)}/avatar-publications${query}`, { parse: parseNovelAvatarPublicationStatus })
    },
    /** 本人的全部作品 */
    listPacks() {
      return transport.request('GET', PACKS, { parse: parseNovelStudioPackList })
    },
    /** @param {{ pack?: Record<string, unknown>, names?: { player: string, guide: string, location: string } }} [body] 无 `pack` 用空白模板（`names` 为界面语言下的默认名），有 `pack` 即导入 */
    createPack(body = {}) {
      return transport.request('POST', PACKS, { body, parse: parseNovelStudioPackDetail })
    },
    /** @param {string} pack_id */
    getPack(pack_id) {
      return transport.request('GET', packPath(pack_id), { parse: parseNovelStudioPackDetail })
    },
    /**
     * 整份内容，保存即生效（C4）。带上载入时作品行的 revision 作 base_revision：库里已被别处改过（含只改音色）时
     * 409 content_conflict（details.current_revision，C21）；已上架作品存不合格内容时 409 content_invalid_for_listed，
     * 两者都不写库。voices 给了就与内容一起写入（C20）。
     * @param {{ pack_id: string, pack: Record<string, unknown>, base_revision: number, play_config?: Record<string, unknown>, cover_asset_id?: string|null }} args
     */
    saveContent({ pack_id, pack, base_revision, play_config, cover_asset_id }) {
      const body = { pack, base_revision }
      if (play_config !== undefined) body.play_config = play_config
      if (cover_asset_id !== undefined) body.cover_asset_id = cover_asset_id
      return transport.request('PUT', `${packPath(pack_id)}/content`, { body, parse: parseNovelContentResponse })
    },
    /**
     * 按语言封面整份替换（草稿），立即生效，不带 revision；返回作品行。
     * @param {{ pack_id: string, cover_locales: Record<string, string> }} args 语言 → 本作品已上传的封面素材 id
     */
    setCoverLocales({ pack_id, cover_locales }) {
      return transport.request('PUT', `${packPath(pack_id)}/cover-locales`, { body: { cover_locales }, parse: parseNovelCoverLocalesResponse })
    },
    /**
     * 严格口径校验，返回全部问题（C5）：给了 pack 就校验它、不保存；不给就校验库里的当前内容。
     * @param {{ pack_id: string, pack?: Record<string, unknown> }} args
     */
    validate({ pack_id, pack }) {
      return transport.request('POST', `${packPath(pack_id)}/validate`, { body: pack === undefined ? {} : { pack }, parse: parseNovelValidateResponse })
    },
    /** @param {{ pack_id: string, tag: string, file_name: string, mime: string, data_url: string }} args */
    uploadAsset({ pack_id, ...body }) {
      return transport.request('POST', `${packPath(pack_id)}/assets`, {
        body,
        parse: parseNovelAssetResponse,
        timeoutMs: ASSET_UPLOAD_TIMEOUT_MS,
      })
    },
    /** @param {{ pack_id: string, base_revision: number, play_config: object }} args */
    saveVoices({ pack_id, base_revision, play_config }) {
      return transport.request('PUT', `${packPath(pack_id)}/voices`, { body: { base_revision, play_config }, parse: parseNovelVoicesResponse })
    },
    listTtsVoices() {
      return transport.request('GET', '/api/novel/studio/tts-voices', { parse: parseNovelTtsVoiceList })
    },
    /** @param {string} pack_id */
    exportPack(pack_id) {
      return transport.request('GET', `${packPath(pack_id)}/export`, { parse: parseNovelStudioExport })
    },
    /** @param {string} pack_id 作者切出的版本，最新在前 */
    listVersions(pack_id) {
      return transport.request('GET', `${packPath(pack_id)}/versions`, { parse: parseNovelStudioVersionList })
    },
    /** 某个版本冻结的内容（C18）。 @param {{ pack_id: string, version_id: string }} args */
    exportVersion({ pack_id, version_id }) {
      return transport.request('GET', `${packPath(pack_id)}/versions/${encodeURIComponent(version_id)}/export`, { parse: parseNovelVersionExport })
    },
    /**
     * 从当前已保存内容切一个不可变版本（内容未通过校验返回 CONFLICT）；note 是可选的版本说明（C24），省略时不带进 body。
     * @param {{ pack_id: string, base_revision: number, note?: string }} args
     */
    publishVersion({ pack_id, base_revision, note }) {
      return transport.request('POST', `${packPath(pack_id)}/versions`, { body: { base_revision, ...(note === undefined ? {} : { note }) }, parse: parseNovelVersionResponse, timeoutMs: AVATAR_PUBLICATION_TIMEOUT_MS })
    },
    /** @param {string} pack_id 作者归档：任意状态 → archived，不可逆 */
    archivePack(pack_id) {
      return transport.request('POST', `${packPath(pack_id)}/archive`, { body: {}, parse: parseNovelArchiveResponse })
    },
    /** @param {string} pack_id */
    deletePack(pack_id) {
      return transport.request('DELETE', packPath(pack_id), { parse: parseNovelDeleteResponse })
    },
  }
}
